import { useEffect } from 'react';

interface PortfolioModalProps {
  caseData: {
    title: string;
    location: string;
    area: string;
    description: string;
    beforeImage: string;
    afterImage: string;
    beforeConditions: string[];
    renovationPoints: string[];
    highlights: string[];
    testimonial: string;
    client: string;
  };
  onClose: () => void;
}

export default function PortfolioModal({ caseData, onClose }: PortfolioModalProps) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="relative w-full max-w-6xl max-h-[90vh] bg-white rounded-3xl overflow-hidden shadow-2xl">
        <button
          onClick={onClose}
          className="absolute top-6 right-6 z-20 w-12 h-12 bg-white rounded-full flex items-center justify-center text-gray-600 hover:text-primary hover:bg-warm-100 transition-all shadow-lg cursor-pointer"
          aria-label="關閉"
        >
          <i className="ri-close-line text-2xl"></i>
        </button>

        <div className="overflow-y-auto max-h-[90vh]">
          <div className="relative h-96 bg-gradient-to-br from-primary to-accent">
            <div className="absolute inset-0 grid grid-cols-2 gap-2 p-8">
              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <div className="absolute top-4 left-4 z-10 px-4 py-2 bg-black/60 text-white text-sm font-medium rounded-full backdrop-blur-sm">
                  改造前
                </div>
                <img
                  src={caseData.beforeImage}
                  alt="改造前"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <div className="absolute top-4 right-4 z-10 px-4 py-2 bg-accent text-white text-sm font-medium rounded-full">
                  改造後
                </div>
                <img
                  src={caseData.afterImage}
                  alt="改造後"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          <div className="p-12">
            <div className="mb-8">
              <h2 className="text-4xl font-bold text-primary mb-4 font-serif">{caseData.title}</h2>
              <div className="flex items-center gap-6 text-base text-gray-600">
                <span className="flex items-center gap-2">
                  <i className="ri-map-pin-line text-accent"></i>
                  {caseData.location}
                </span>
                <span className="flex items-center gap-2">
                  <i className="ri-ruler-line text-accent"></i>
                  {caseData.area}
                </span>
              </div>
            </div>

            <div className="prose max-w-none mb-12">
              <p className="text-lg text-gray-700 leading-relaxed">{caseData.description}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="bg-warm-50 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-primary mb-4 flex items-center gap-2">
                  <i className="ri-error-warning-line text-accent"></i>
                  改造前狀況
                </h3>
                <ul className="space-y-2">
                  {caseData.beforeConditions.map((condition, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                      <i className="ri-close-circle-line text-red-500 mt-0.5 flex-shrink-0"></i>
                      <span>{condition}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-warm-50 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-primary mb-4 flex items-center gap-2">
                  <i className="ri-tools-line text-accent"></i>
                  改造重點
                </h3>
                <ul className="space-y-2">
                  {caseData.renovationPoints.map((point, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                      <i className="ri-arrow-right-circle-line text-accent mt-0.5 flex-shrink-0"></i>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-accent/10 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-primary mb-4 flex items-center gap-2">
                  <i className="ri-star-line text-accent"></i>
                  成果亮點
                </h3>
                <ul className="space-y-2">
                  {caseData.highlights.map((highlight, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                      <i className="ri-checkbox-circle-line text-green-600 mt-0.5 flex-shrink-0"></i>
                      <span>{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="bg-gradient-to-br from-primary to-accent rounded-2xl p-8 text-white mb-8">
              <div className="flex items-start gap-4">
                <i className="ri-double-quotes-l text-4xl text-white/40"></i>
                <div className="flex-1">
                  <p className="text-lg leading-relaxed mb-4">{caseData.testimonial}</p>
                  <p className="text-sm text-white/80">— {caseData.client}</p>
                </div>
              </div>
            </div>

            <div className="text-center space-y-4">
              <p className="text-xl font-bold text-primary">讓您的家也有同樣的改變嗎？</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="https://lin.ee/JAWndnI"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-4 bg-primary text-white rounded-full text-base font-medium hover:bg-primary/90 transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-message-3-line"></i>
                  加入LINE諮詢
                </a>
                <a
                  href="https://go.hotcake.app/g/VB3HEP"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-4 bg-white text-primary rounded-full text-base font-medium border-2 border-primary hover:bg-warm-50 transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-calendar-line"></i>
                  預約到府評估
                </a>
              </div>
            </div>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-xs text-gray-500 text-center">
                關鍵字：台南室內裝修、台南舊屋改造、浴室翻新、室內設計、軟裝設計、統包工程
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}